import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { 
  fetchBranchByName, 
  fetchProductsByBranch, 
  createBranch, 
  listBranches, 
  updateBranch, 
  deleteBranch 
} from '../lib/api';
import { toast } from 'react-toastify';
import { FiPlus, FiEdit2, FiTrash2, FiMapPin } from 'react-icons/fi';

const Branches = () => {
  const { branchName: branchNameParam } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [branchName, setBranchName] = useState('');
  const [allBranches, setAllBranches] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingBranch, setEditingBranch] = useState(null);

  const loadBranches = async () => {
    try {
      setLoading(true);
      const list = await listBranches();
      setAllBranches(list);
    } catch (error) {
      toast.error('Failed to load branches');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBranches();
  }, []);

  useEffect(() => {
    if (branchNameParam) {
      (async () => {
        try {
          const br = await fetchBranchByName(branchNameParam);
          if (br?.id) {
            const prods = await fetchProductsByBranch(br.id);
            setProducts(prods);
          }
        } catch (error) {
          toast.error('Failed to load branch details');
        }
      })();
    }
  }, [branchNameParam]);

  const handleAddBranch = async (e) => {
    e.preventDefault();
    if (!branchName.trim()) {
      toast.error('Branch name is required');
      return;
    }
    try {
      setLoading(true);
      console.log('Creating branch with name:', branchName);
      await createBranch(branchName);
      toast.success('Branch added successfully!');
      setBranchName('');
      setShowAddModal(false);
      await loadBranches();
    } catch (error) {
      console.error('Full error details:', error);
      const errorMessage = error?.message || error?.error_description || 'Failed to add branch';
      toast.error(`Failed to add branch: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateBranch = async (e) => {
    e.preventDefault();
    if (!editingBranch || !branchName.trim()) {
      toast.error('Branch name is required');
      return;
    }
    try {
      setLoading(true);
      await updateBranch(editingBranch.id, {
        branch_name: branchName
      });
      toast.success('Branch updated successfully!');
      setBranchName('');
      setEditingBranch(null);
      await loadBranches();
    } catch (error) {
      console.error('Full error details:', error);
      const errorMessage = error?.message || error?.error_description || 'Failed to update branch';
      toast.error(`Failed to update branch: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteBranch = async (id, name) => {
    if (!window.confirm(`Are you sure you want to delete branch "${name}"?`)) return;
    try {
      setLoading(true);
      await deleteBranch(id);
      toast.success('Branch deleted successfully!');
      await loadBranches();
    } catch (error) {
      toast.error('Failed to delete branch');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const startEdit = (branchData) => {
    setEditingBranch(branchData);
    setBranchName(branchData.branch_name || branchData.name || '');
    setShowAddModal(true);
  };

  const cancelEdit = () => {
    setEditingBranch(null);
    setBranchName('');
    setShowAddModal(false);
  };

  const testConnection = async () => {
    try {
      toast.info('Testing database connection...');
      const testBranch = await createBranch(`Test_${Date.now()}`);
      toast.success(`✅ Test successful! Created branch: ${testBranch.branch_name}`);
      await loadBranches();
    } catch (error) {
      console.error('Connection test failed:', error);
      let errorMsg = 'Connection test failed';
      
      if (error.message) {
        errorMsg += `\n${error.message}`;
      }
      if (error.code) {
        errorMsg += `\nCode: ${error.code}`;
      }
      if (error.hint) {
        errorMsg += `\nHint: ${error.hint}`;
      }
      
      toast.error(errorMsg, { autoClose: 10000 });
      
      // Show detailed instructions
      if (error.message?.includes('row-level security') || error.message?.includes('policy')) {
        toast.warning('🔒 RLS Policy Issue Detected! Check the BRANCH_TROUBLESHOOTING.md file for solutions.', { autoClose: false });
      }
    }
  };

  // Branch Detail View
  if (branchNameParam) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <div className="mb-6">
          <button
            onClick={() => navigate('/branches')}
            className="text-blue-600 hover:text-blue-800 mb-4 flex items-center gap-2"
          >
            ← Back to Branches
          </button>
          <h1 className="text-3xl font-bold">
            {branchNameParam.charAt(0).toUpperCase() + branchNameParam.slice(1)}
          </h1>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold mb-4">Products in this Branch</h2>
          {products.length === 0 ? (
            <p className="text-gray-500">No products found in this branch.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Product Name
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Price
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Stock
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Category
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {products.map((product) => (
                    <tr key={product.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">{product.product_name || product.name}</td>
                      <td className="px-4 py-3">${product.initial_price || 0}</td>
                      <td className="px-4 py-3">{product.quantity || 0}</td>
                      <td className="px-4 py-3">{product.category_id || 'N/A'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Main Branches List View
  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Branch Management</h1>
          <p className="text-gray-600 mt-1">Manage all your store branches</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={testConnection}
            className="bg-gray-600 text-white px-4 py-3 rounded-lg hover:bg-gray-700 flex items-center gap-2 transition-colors shadow-md text-sm"
            title="Test database connection"
          >
            🔧 Test Connection
          </button>
          <button
            onClick={() => {
              setEditingBranch(null);
              setBranchName('');
              setShowAddModal(true);
            }}
            className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 flex items-center gap-2 transition-colors shadow-md"
          >
            <FiPlus className="text-xl" />
            Add Branch
          </button>
        </div>
      </div>

      {loading && !showAddModal ? (
        <div className="flex items-center justify-center p-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {allBranches.map((branchItem) => (
            <div
              key={branchItem.id}
              className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow p-6 border border-gray-200"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="bg-blue-100 p-3 rounded-full">
                    <FiMapPin className="text-blue-600 text-xl" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-800">
                      {branchItem.name || branchItem.branch_name}
                    </h3>
                    {branchItem.branch_name && branchItem.branch_name !== branchItem.name && (
                      <p className="text-sm text-gray-500">{branchItem.branch_name}</p>
                    )}
                  </div>
                </div>
              </div>

              <div className="text-sm text-gray-500 mb-4">
                <p>Created: {new Date(branchItem.created_at).toLocaleDateString()}</p>
              </div>

              <div className="flex gap-2">
                <button
                  onClick={() => navigate(`/branches/${branchItem.name || branchItem.branch_name}`)}
                  className="flex-1 bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  View Details
                </button>
                <button
                  onClick={() => startEdit(branchItem)}
                  className="bg-blue-100 text-blue-700 p-2 rounded-lg hover:bg-blue-200 transition-colors"
                  title="Edit Branch"
                >
                  <FiEdit2 />
                </button>
                <button
                  onClick={() => handleDeleteBranch(branchItem.id, branchItem.name || branchItem.branch_name)}
                  className="bg-red-100 text-red-700 p-2 rounded-lg hover:bg-red-200 transition-colors"
                  title="Delete Branch"
                >
                  <FiTrash2 />
                </button>
              </div>
            </div>
          ))}

          {allBranches.length === 0 && (
            <div className="col-span-full text-center py-12">
              <FiMapPin className="text-gray-300 text-6xl mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-600 mb-2">No branches yet</h3>
              <p className="text-gray-500">Get started by adding your first branch</p>
            </div>
          )}
        </div>
      )}

      {/* Add/Edit Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h2 className="text-2xl font-bold mb-6">
              {editingBranch ? 'Edit Branch' : 'Add New Branch'}
            </h2>
            <form onSubmit={editingBranch ? handleUpdateBranch : handleAddBranch} className="space-y-4">
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">
                  Branch Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={branchName}
                  onChange={(e) => setBranchName(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., Mumbai, Delhi, Bangalore"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  Enter a unique name for this branch
                </p>
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={cancelEdit}
                  className="flex-1 bg-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-300 transition-colors"
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={loading}
                >
                  {loading ? 'Saving...' : editingBranch ? 'Update' : 'Add Branch'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Branches; 